<template>
  <div class="custom-progress-bar" :class="barClass">
    <div class="progress-fill" :style="{ width: `${percentage}%` }"></div>
    <span class="progress-text-left">{{ leftText }}</span>
    <span class="progress-text-right">{{ rightText }}</span>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  // Core props for calculation
  value: {
    type: Number,
    default: 0,
  },
  total: {
    type: Number,
    default: 100,
  },

  // Display text props
  leftText: {
    type: String,
    default: "",
  },
  rightText: {
    type: String,
    default: "",
  },

  // Formatting props
  useDays: {
    type: Boolean,
    default: true,
  },

  // Style props
  theme: {
    type: String,
    default: "auto",
    validator: (value) =>
      ["auto", "success", "info", "warning", "danger"].includes(value),
  },

  // For renewal bars - inverse the color logic
  isRenewal: {
    type: Boolean,
    default: false,
  },
});

// Calculate percentage
const percentage = computed(() => {
  if (!props.total || props.total === 0) return 0;
  const pct = (props.value / props.total) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
});

// Determine bar class based on percentage or theme
const barClass = computed(() => {
  if (props.theme !== "auto") {
    return `is-${props.theme}`;
  }

  const pct = percentage.value;

  if (props.isRenewal) {
    // For renewal bars - we're showing time remaining
    // More time remaining (higher percentage) is good (green)
    if (pct >= 50) return "is-success";
    if (pct >= 25) return "is-primary";
    if (pct >= 10) return "is-warning";
    return "is-danger";
  } else {
    // For usage bars - less usage is good (green)
    if (pct >= 90) return "is-danger";
    if (pct >= 75) return "is-warning";
    if (pct >= 50) return "is-primary";
    return "is-success";
  }
});
</script>

<style lang="scss" scoped>
.custom-progress-bar {
  position: relative;
  height: 2rem;
  border-radius: 4px;
  overflow: hidden;
  background-color: transparent;

  .progress-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    transition: width 0.3s ease;
    border-radius: 3px;
  }

  .progress-text-left {
    position: absolute;
    top: 50%;
    left: 0.75rem;
    transform: translateY(-50%);
    font-size: 0.85rem;
    font-weight: 600;
    pointer-events: none;
    z-index: 2;
    white-space: nowrap;
  }

  .progress-text-right {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    font-size: 0.85rem;
    font-weight: 600;
    pointer-events: none;
    z-index: 2;
    white-space: nowrap;
  }

  // Success theme (green) - transparent background with colored border and semi-transparent fill
  &.is-success {
    border: 1px solid
      hsl(
        var(--bulma-success-h),
        var(--bulma-success-s),
        var(--bulma-success-l)
      );

    .progress-fill {
      background-color: hsla(
        var(--bulma-success-h),
        var(--bulma-success-s),
        var(--bulma-success-l),
        0.2
      );
    }

    .progress-text-left,
    .progress-text-right {
      color: hsl(
        var(--bulma-success-h),
        var(--bulma-success-s),
        var(--bulma-success-l)
      );
    }
  }

  // Info theme (blue)
  &.is-primary {
    border: 1px solid
      hsl(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        var(--bulma-primary-l)
      );

    .progress-fill {
      background-color: hsla(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        var(--bulma-primary-l),
        0.2
      );
    }

    .progress-text-left,
    .progress-text-right {
      color: hsl(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        var(--bulma-primary-l)
      );
    }
  }

  // Warning theme (yellow/orange)
  &.is-warning {
    border: 1px solid
      hsl(
        var(--bulma-warning-h),
        var(--bulma-warning-s),
        var(--bulma-warning-l)
      );

    .progress-fill {
      background-color: hsla(
        var(--bulma-warning-h),
        var(--bulma-warning-s),
        var(--bulma-warning-l),
        0.2
      );
    }

    .progress-text-left,
    .progress-text-right {
      color: hsl(
        var(--bulma-warning-h),
        var(--bulma-warning-s),
        var(--bulma-warning-l)
      );
    }
  }

  // Danger theme (red)
  &.is-danger {
    border: 1px solid
      hsl(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l));

    .progress-fill {
      background-color: hsla(
        var(--bulma-danger-h),
        var(--bulma-danger-s),
        var(--bulma-danger-l),
        0.2
      );
    }

    .progress-text-left,
    .progress-text-right {
      color: hsl(
        var(--bulma-danger-h),
        var(--bulma-danger-s),
        var(--bulma-danger-l)
      );
    }
  }
}
</style>
